import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import type PresidentViewProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownEditor,
    TextAreaInput,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    CameraIcon,
    Trash2Icon,
    ImageIcon,
    SaveIcon
} from "lucide-react-native";
import DriveSelectorModal from "../../../../components/driveSelectorModal";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const PresidentView = ({
    refreshKey
}: PresidentViewProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const photoInputRef = useRef<HTMLInputElement>(null);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDriveModalVisible,
        setIsDriveModalVisible
    ] = useState<boolean>(false);

    const [
        photoFile,
        setPhotoFile
    ] = useState<File | null>(null);

    const [
        photoPreview,
        setPhotoPreview
    ] = useState<string>("");

    const [
        fullName,
        setFullName
    ] = useState<string>("");

    const [
        position,
        setPosition
    ] = useState<string>("");

    const [
        shortContent,
        setShortContent
    ] = useState<string>("");

    const [
        content,
        setContent
    ] = useState<string>("");

    const [
        profilePhotoPath,
        setProfilePhotoPath
    ] = useState<string>("");

    const [
        editorKey,
        setEditorKey
    ] = useState<number>(0);

    const getImageUrl = (path?: string) => {
        if(!path) {
            return "";
        }
        if(path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
            return path;
        }
        return `${process.env.STORAGE_URL}/uploads${path.startsWith("/") ? "" : "/"}${path}`;
    };

    const normalizeMarkdownImages = (text: string) => {
        if(!text) {
            return "";
        }
        return text.replace(/!\[(.*?)\]\s*\((.*?)\)/g, "\n[image]($2)<100%x300>--contain--\n");
    };

    const uploadPhoto = async (file: File): Promise<string> => {
        const tokenResult = await RESTServiceContext.action("getUploadTokenForContentPhoto", {
            fileType: file.type || "image/jpeg",
            fileName: file.name,
            fileSize: file.size
        });
        const uploadToken = tokenResult.payload?.token;
        if(!uploadToken) {
            throw new Error("No upload token received");
        }
        const formData = new FormData();
        formData.append("file", file);
        const uploadResponse = await fetch(`${process.env.STORAGE_URL}/upload`, {
            headers: {
                Authorization: uploadToken
            },
            body: formData,
            method: "POST"
        });
        if(!uploadResponse.ok) {
            throw new Error("Storage upload failed");
        }
        const uploadJson = await uploadResponse.json();
        return uploadJson?.payload?.key || "";
    };

    const fetchPresident = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await RESTServiceContext.action("getPresident", null);
            if(response?.payload) {
                const data = response.payload;
                setFullName(data.fullName || "");
                setPosition(data.position || "");
                setShortContent(data.shortContent || "");
                setContent(normalizeMarkdownImages(data.content || ""));
                setProfilePhotoPath(data.profilePhotoPath || "");
                setPhotoFile(null);
                setPhotoPreview("");
                setEditorKey(prev => prev + 1);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("president-fetch-error")
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        localize
    ]);

    useEffect(() => {
        fetchPresident();
    }, [
        fetchPresident,
        refreshKey
    ]);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if(file) {
            const maxFileSize = 5 * 1024 * 1024;
            if(file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemovePhoto = () => {
        setProfilePhotoPath("");
        setPhotoPreview("");
        setPhotoFile(null);
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
    };

    const handleDriveFileSelect = (url: string) => {
        setIsDriveModalVisible(false);
        const currentContent = content || "";
        const imageMarkdown = `\n[image](${url})<100%x300>--contain--\n`;
        setContent(currentContent + imageMarkdown);
        setEditorKey(prev => prev + 1);
    };

    const handleSave = async () => {
        if(!fullName.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("president-name-required")
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let finalPhotoPath = profilePhotoPath;
            if(photoFile) {
                const uploadedKey = await uploadPhoto(photoFile);
                if(uploadedKey) {
                    finalPhotoPath = uploadedKey;
                }
            }

            const response = await RESTServiceContext.action("updatePresident", {
                profilePhotoPath: finalPhotoPath || undefined,
                content: normalizeMarkdownImages(content),
                shortContent,
                position,
                fullName
            });

            if(response?.isError) {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("president-save-error")
                });
                return;
            }

            if(photoFile) {
                setPhotoFile(null);
                setProfilePhotoPath(finalPhotoPath);
            }

            NCoreUIKitSnackBar.open({
                title: localize("president-save-success")
            });
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("president-save-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderPhotoSection = () => {
        const displayPhotoUrl = photoPreview || getImageUrl(profilePhotoPath);

        return <View
            style={[
                stylesheet.photoContainer,
                {
                    marginBottom: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <input
                style={stylesheet.photoInput}
                onChange={handlePhotoSelect}
                ref={photoInputRef}
                accept="image/*"
                type="file"
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoUploadBox,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderColor: colors.project.brandBorder,
                        borderRadius: radiuses.md
                    }
                ]}
                onPress={() => photoInputRef.current?.click()}
            >
                {
                    displayPhotoUrl ?
                        <SmartCoverImage
                            containerStyle={stylesheet.photoPreview}
                            imageStyle={stylesheet.photoPreview}
                            url={displayPhotoUrl}
                        />
                    :
                        <View
                            style={[
                                stylesheet.photoPlaceholderContent,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <CameraIcon
                                color={colors.content.text.disabled}
                                size={32}
                            />
                            <Text
                                variant="bodySmallSize"
                                color="disabled"
                            >
                                {localize("content-select-photo")}
                            </Text>
                        </View>
                }
            </TouchableOpacity>
            <View
                style={[
                    stylesheet.photoButtonsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    onPress={() => photoInputRef.current?.click()}
                    customTextColor={colors.project.brandWhite}
                    title={localize("content-select-photo")}
                    icon={({
                        size
                    }) => <CameraIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                {Boolean(displayPhotoUrl) && <Button
                    title={localize("content-remove-photo")}
                    icon={({
                        size
                    }) => <Trash2Icon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    onPress={handleRemovePhoto}
                    spreadBehaviour="free"
                    variant="filled"
                    type="danger"
                />}
            </View>
        </View>;
    };

    if(isLoading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.project.gold}
                size="large"
            />
        </View>;
    }

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.formCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderPhotoSection()}
            <TextInput
                placeholder={localize("president-fullname-placeholder")}
                title={localize("president-fullname-label")}
                onChangeText={setFullName}
                spreadBehaviour="stretch"
                value={fullName}
            />
            <TextInput
                placeholder={localize("president-position-placeholder")}
                title={localize("president-position-label")}
                onChangeText={setPosition}
                spreadBehaviour="stretch"
                value={position}
            />
            <TextAreaInput
                placeholder={localize("president-shortcontent-placeholder")}
                title={localize("president-shortcontent-label")}
                key={`shortContent-${editorKey}`}
                onChangeText={setShortContent}
                isUpdateOnRealtime={true}
                spreadBehaviour="stretch"
                value={shortContent}
            />
            <View
                style={[
                    stylesheet.editorSection,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="titleSmallSize"
                    color="high"
                >
                    {localize("president-content-label")}
                </Text>
                <MarkdownEditor
                    key={`president-editor-${editorKey}`}
                    onChangeText={setContent}
                    spreadBehaviour="stretch"
                    initialValue={content}
                    defaultMode="live"
                />
                <Button
                    title={localize("generic-info-add-image-drive")}
                    onPress={() => setIsDriveModalVisible(true)}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <ImageIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
            </View>
            <View
                style={stylesheet.actionContainer}
            >
                <Button
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <SaveIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    title={localize("save")}
                    isLoading={isSubmitting}
                    spreadBehaviour="free"
                    onPress={handleSave}
                    variant="filled"
                    type="primary"
                />
            </View>
        </View>
        <DriveSelectorModal
            onClose={() => setIsDriveModalVisible(false)}
            onSelect={handleDriveFileSelect}
            isVisible={isDriveModalVisible}
        />
    </View>;
};
export default PresidentView;
